'use client';

import Link from 'next/link';
import Image from 'next/image';
import Script from 'next/script';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { display } from '../lib/fonts';

const companies = ['Wipro', 'Infosys', 'Accenture', 'Capgemini', 'TCS', 'Cognizant', 'LTIMindtree', 'Tech Mahindra', 'HCL', 'Mphasis', 'Persistent', 'KPMG'];

const cities = ['Bangalore', 'Noida', 'Pune', 'Chennai', 'Delhi', 'Hyderabad', 'Gurgaon'];

const faqData = [
    { q: 'Is the job search platform completely free?', a: 'Yes! Our core job search features are completely free. We believe everyone deserves access to quality job opportunities. Premium features like advanced AI coaching are available for enhanced users.' },
    { q: 'How does the AI mock interview system work?', a: 'Our AI analyzes your target role and industry to generate relevant interview questions. You can practice in a realistic interview environment and receive instant feedback on your responses.' },
    { q: 'What makes your referral system different?', a: 'Unlike traditional job boards, our referral system connects you directly with employees at target companies. This gives you insider access and increases your chances of getting hired through personal recommendations.' },
    { q: 'How long does it typically take to find a job?', a: 'Our users typically see significant improvements within 2–4 weeks. Those who actively use AI interview practice and the referral network often find positions much faster.' },
    { q: 'Can I use the platform for career transitions?', a: 'Absolutely! Our AI can help identify transferable skills and suggest relevant opportunities in new industries. Career coaching features provide guidance for successful transitions.' },
];

const ICON_PATHS = {
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    check: 'M5 13l4 4L19 7',
    arrow: 'M17 8l4 4m0 0l-4 4m4-4H3',
    pin: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z',
    chevron: 'M19 9l-7 7-7-7',
    briefcase: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
};

function Icon({ name, className = 'w-5 h-5', strokeWidth = 1.8 }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={ICON_PATHS[name]} />
        </svg>
    );
}

const SECTION = 'py-20 md:py-28';
const WRAP = 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8';
const H2 = `${display.className} text-3xl sm:text-4xl lg:text-5xl font-bold tracking-[-0.025em] text-slate-900 dark:text-white text-balance`;
const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-customBlue';
const BTN_PRIMARY = `inline-flex items-center justify-center gap-2 rounded-xl bg-customBlue px-6 min-h-[48px] text-base font-semibold text-slate-950 transition hover:brightness-95 active:scale-[0.98] ${FOCUS}`;
const BTN_SECONDARY = `inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 dark:border-gray-700 px-6 min-h-[48px] text-base font-semibold text-slate-800 dark:text-slate-100 transition hover:bg-slate-100 dark:hover:bg-gray-800 ${FOCUS}`;

// ── Hero product preview ────────────────────────────────────────────────
// A cropped look at the referral board. Sample listings only, so it is hidden from assistive tech.
const previewJobs = [
    { title: 'Senior Frontend Engineer', meta: 'Product company · Hybrid · Bangalore', status: 'available', active: true },
    { title: 'Data Analyst', meta: 'IT services · On-site · Bangalore', status: 'available' },
    { title: 'QA Engineer, Automation', meta: 'Fintech · Hybrid · Bangalore', status: 'sent' },
    { title: 'DevOps Engineer', meta: 'SaaS · Remote', status: 'available' },
    { title: 'Product Manager', meta: 'E-commerce · Hybrid · Bangalore', status: 'available' },
];

const timeline = [
    { label: 'Request sent', state: 'done' },
    { label: 'Employee submits your resume', state: 'done' },
    { label: 'Recruiter reviews your profile', state: 'current' },
    { label: 'Interview', state: 'todo' },
];

function HeroPreview() {
    return (
        <div aria-hidden="true" className="relative mx-auto mt-14 md:mt-20 w-full max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="relative h-[400px] md:h-[460px] overflow-hidden rounded-t-2xl border border-b-0 border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-[0_-20px_80px_-30px_rgba(0,180,198,0.35)]">
                <div className="flex items-center gap-5 border-b border-slate-200 dark:border-gray-800 px-4 sm:px-5 py-3 text-sm">
                    <span className="whitespace-nowrap font-semibold text-slate-900 dark:text-white">Referral jobs</span>
                    <span className="text-slate-400">Saved</span>
                    <span className="hidden sm:inline whitespace-nowrap text-slate-400">My requests</span>
                    <span className="ml-auto flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 dark:border-gray-700 px-2.5 py-1 text-xs text-slate-600 dark:text-slate-300">
                        <Icon name="pin" className="w-3.5 h-3.5 text-teal-600 dark:text-customBlue" strokeWidth={2} /> Bangalore
                    </span>
                </div>

                <div className="grid md:grid-cols-[1.2fr_1fr]">
                    <ul className="divide-y divide-slate-100 dark:divide-gray-800">
                        {previewJobs.map((job) => (
                            <li key={job.title} className={`flex items-center gap-3 px-4 sm:px-5 py-3.5 ${job.active ? 'bg-teal-50/70 dark:bg-customBlue/10 shadow-[inset_3px_0_0_#00b4c6]' : ''}`}>
                                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-gray-800 text-slate-500 dark:text-slate-400">
                                    <Icon name="briefcase" className="w-5 h-5" />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-semibold text-slate-900 dark:text-white">{job.title}</span>
                                    <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{job.meta}</span>
                                </span>
                                {job.status === 'available' ? (
                                    <span className="flex flex-shrink-0 items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> <span className="hidden sm:inline">Referral available</span><span className="sm:hidden">Open</span>
                                    </span>
                                ) : (
                                    <span className="flex-shrink-0 text-xs font-medium text-slate-500 dark:text-slate-400">Request sent</span>
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className="hidden md:block border-l border-slate-200 dark:border-gray-800 p-6">
                        <p className="text-lg font-semibold text-slate-900 dark:text-white">Senior Frontend Engineer</p>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Product company · Hybrid · Bangalore</p>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            {['React', 'TypeScript', 'Referral available'].map((t) => (
                                <span key={t} className="rounded-md bg-slate-100 dark:bg-gray-800 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-300">{t}</span>
                            ))}
                        </div>
                        <p className="mt-6 text-sm font-semibold text-slate-900 dark:text-white">Your referral</p>
                        <ol className="mt-3">
                            {timeline.map((step, i) => (
                                <li key={step.label} className="relative flex gap-3 pb-4 last:pb-0">
                                    {i < timeline.length - 1 && <span className={`absolute left-[9px] top-5 h-full w-px ${step.state === 'done' ? 'bg-customBlue' : 'bg-slate-200 dark:bg-gray-700'}`} />}
                                    <span className={`relative z-10 mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
                                        step.state === 'done' ? 'bg-customBlue text-slate-950'
                                        : step.state === 'current' ? 'border-2 border-customBlue bg-white dark:bg-gray-900'
                                        : 'border-2 border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900'}`}>
                                        {step.state === 'done' && <Icon name="check" className="w-3 h-3" strokeWidth={3} />}
                                    </span>
                                    <span className={`text-sm ${step.state === 'todo' ? 'text-slate-400' : 'text-slate-700 dark:text-slate-200'}`}>{step.label}</span>
                                </li>
                            ))}
                        </ol>
                        <p className="mt-5 text-xs text-slate-400">Sample listings, shown for illustration.</p>
                    </div>
                </div>

                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-white dark:from-gray-950 to-transparent" />
            </div>
        </div>
    );
}

// ── What a referral changes ────────────────────────────────────────────
const compareRows = [
    { label: 'Who reads it first', alone: 'Applicant tracking software that filters by keywords.', referred: 'A recruiter, with a note from an employee attached.' },
    { label: 'What it says about you', alone: 'Only what is written on the page.', referred: 'Someone inside the company is willing to put their name next to yours.' },
    { label: 'What you still have to do', alone: 'Clear the interviews on merit.', referred: 'Clear the interviews on merit. A referral gets you seen, not hired.' },
];

function CompareTable() {
    return (
        <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <div className="hidden md:grid grid-cols-[200px_1fr_1fr] border-b border-slate-200 dark:border-gray-800 text-sm font-semibold">
                <div className="p-5" />
                <div className="p-5 text-slate-500 dark:text-slate-400">Applying alone</div>
                <div className="p-5 text-teal-800 dark:text-customBlue bg-teal-50/60 dark:bg-customBlue/10">With a referral</div>
            </div>
            {compareRows.map((row) => (
                <div key={row.label} className="grid md:grid-cols-[200px_1fr_1fr] border-b border-slate-200 dark:border-gray-800 last:border-b-0">
                    <div className="px-5 pt-5 md:py-5 text-sm font-semibold text-slate-900 dark:text-white">{row.label}</div>
                    <div className="px-5 py-3 md:py-5 text-base text-slate-600 dark:text-slate-400">
                        <span className="mb-1 block text-xs font-semibold text-slate-400 md:hidden">Applying alone</span>
                        {row.alone}
                    </div>
                    <div className="px-5 py-4 md:py-5 text-base text-slate-800 dark:text-slate-100 bg-teal-50/60 dark:bg-customBlue/10">
                        <span className="mb-1 block text-xs font-semibold text-teal-800 dark:text-customBlue md:hidden">With a referral</span>
                        {row.referred}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function HomeContent({ blogSection = null }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [location, setLocation] = useState('Bangalore');
    const [locationOpen, setLocationOpen] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);
    const locationRef = React.useRef(null);
    const router = useRouter();

    // Close location dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (locationRef.current && !locationRef.current.contains(e.target)) {
                setLocationOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, []);

    const handleHeroSearch = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            router.push(`/job_search?job_search=${encodeURIComponent(searchTerm)}&location=${encodeURIComponent(location)}`);
        }
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(item => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": { "@type": "Answer", "text": item.a }
        }))
    };

    return (
        <>
            <Script strategy="afterInteractive" src="https://www.googletagmanager.com/gtag/js?id=G-1XMB1M6104" />
            <Script id="google-analytics" strategy="afterInteractive" dangerouslySetInnerHTML={{ __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-1XMB1M6104');` }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

            <main className="flex w-full flex-col overflow-x-hidden bg-white dark:bg-gray-950 text-slate-900 dark:text-slate-100">

                {/* ═════════ HERO ═════════ */}
                <section className="relative bg-white dark:bg-gray-950 pt-14 md:pt-24">
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0"
                        style={{
                            backgroundImage: 'linear-gradient(to right, rgba(100,116,139,0.10) 1px, transparent 1px), linear-gradient(to bottom, rgba(100,116,139,0.10) 1px, transparent 1px)',
                            backgroundSize: '64px 64px',
                            maskImage: 'radial-gradient(ellipse 70% 55% at 50% 0%, #000 30%, transparent 100%)',
                            WebkitMaskImage: 'radial-gradient(ellipse 70% 55% at 50% 0%, #000 30%, transparent 100%)',
                        }}
                    />

                    <div className={`${WRAP} relative`}>
                        <div className="mx-auto max-w-4xl text-center">
                            <h1 className={`${display.className} text-[2.75rem] leading-[1.04] sm:text-6xl lg:text-[4.75rem] font-bold tracking-[-0.03em] text-slate-950 dark:text-white text-balance`}>
                                Don&apos;t just apply.<br />Get referred.
                            </h1>

                            <p className="mx-auto mt-6 max-w-2xl text-lg sm:text-xl leading-relaxed text-slate-600 dark:text-slate-400">
                                A referral puts your resume in front of a recruiter with an employee&apos;s name behind it. Find roles at companies that are hiring in India and ask a verified employee to refer you.
                            </p>

                            {/* Search */}
                            <form onSubmit={handleHeroSearch} className="mx-auto mt-10 w-full max-w-3xl text-left">
                                <div className="rounded-2xl md:rounded-full border border-slate-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-1.5 shadow-sm focus-within:border-customBlue focus-within:ring-4 focus-within:ring-customBlue/15 transition">
                                    <div className="flex flex-col gap-1.5 md:flex-row md:items-center">
                                        <div className="relative flex-1 min-w-0">
                                            <span className="pointer-events-none absolute inset-y-0 left-4 md:left-5 flex items-center text-slate-400 dark:text-slate-500">
                                                <Icon name="search" className="w-5 h-5" strokeWidth={2} />
                                            </span>
                                            <label htmlFor="hero-search" className="sr-only">Job title, skills, or company</label>
                                            <input
                                                id="hero-search"
                                                type="text"
                                                inputMode="search"
                                                enterKeyHint="search"
                                                autoComplete="off"
                                                placeholder="Job title, skills, or company"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="w-full min-h-[52px] rounded-xl md:rounded-full bg-transparent pl-12 md:pl-14 pr-4 text-base text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 outline-none"
                                            />
                                        </div>

                                        <div className="hidden md:block h-7 w-px bg-slate-200 dark:bg-gray-700" />

                                        <div ref={locationRef} className="relative md:w-52">
                                            <button
                                                type="button"
                                                onClick={() => setLocationOpen(o => !o)}
                                                aria-haspopup="listbox"
                                                aria-expanded={locationOpen}
                                                className={`flex w-full min-h-[52px] items-center gap-2.5 rounded-xl md:rounded-full px-4 text-base text-slate-800 dark:text-slate-100 transition hover:bg-slate-50 dark:hover:bg-gray-800 border-t border-slate-100 dark:border-gray-800 md:border-t-0 ${FOCUS}`}
                                            >
                                                <Icon name="pin" className="w-4 h-4 flex-shrink-0 text-teal-600 dark:text-customBlue" strokeWidth={2} />
                                                <span className="flex-1 truncate text-left font-medium">{location}</span>
                                                <Icon name="chevron" className={`w-4 h-4 flex-shrink-0 text-slate-400 transition-transform duration-200 ${locationOpen ? 'rotate-180' : ''}`} strokeWidth={2} />
                                            </button>

                                            {locationOpen && (
                                                <div role="listbox" className="absolute left-0 top-full z-50 mt-2 max-h-64 w-full min-w-[12rem] overflow-auto rounded-xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 py-1.5 shadow-lg">
                                                    {cities.map(city => (
                                                        <button
                                                            key={city}
                                                            type="button"
                                                            role="option"
                                                            aria-selected={location === city}
                                                            onClick={() => { setLocation(city); setLocationOpen(false); }}
                                                            className={`flex w-full min-h-[44px] items-center justify-between px-4 text-left text-sm transition-colors ${
                                                                location === city
                                                                    ? 'bg-teal-50 dark:bg-customBlue/10 font-semibold text-teal-800 dark:text-customBlue'
                                                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800'
                                                            }`}
                                                        >
                                                            {city}
                                                            {location === city && <Icon name="check" className="w-4 h-4" strokeWidth={2.5} />}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <button type="submit" className={`${BTN_PRIMARY} md:rounded-full md:px-8`}>
                                            Search Jobs
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                                    <span className="mr-1 text-sm text-slate-500 dark:text-slate-400">Popular:</span>
                                    {['Software Engineer', 'Data Analyst', 'Product Manager', 'DevOps'].map(tag => (
                                        <button key={tag} type="button" onClick={() => setSearchTerm(tag)}
                                            className={`min-h-[36px] rounded-full border border-slate-200 dark:border-gray-800 bg-white/70 dark:bg-gray-900/60 px-3.5 text-sm text-slate-600 dark:text-slate-300 transition hover:border-customBlue hover:text-teal-700 dark:hover:text-customBlue ${FOCUS}`}>
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                            </form>

                            <p className="mt-8 text-sm text-slate-500 dark:text-slate-400">
                                Free for job seekers. Verified employees. Never pay for a referral.
                            </p>
                        </div>
                    </div>

                    <HeroPreview />
                </section>

                {/* ═════════ COMPANY STRIP ═════════ */}
                <section aria-label="Companies our referrers work at" className="border-b border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950 pb-10 pt-12">
                    <p className="mb-5 text-center text-sm text-slate-500 dark:text-slate-400">Trusted by employees at leading companies</p>
                    <div className="relative overflow-hidden">
                        <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 sm:w-28 bg-gradient-to-r from-white dark:from-gray-950 to-transparent" />
                        <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 sm:w-28 bg-gradient-to-l from-white dark:from-gray-950 to-transparent" />
                        <div className="flex w-max animate-scroll whitespace-nowrap motion-reduce:animate-none">
                            {[...companies, ...companies].map((company, i) => (
                                <span key={i} className="select-none px-6 sm:px-9 text-lg sm:text-xl font-semibold text-slate-400 dark:text-slate-600">
                                    {company}
                                </span>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ═════════ WHAT A REFERRAL CHANGES ═════════ */}
                <section className={`${SECTION} bg-white dark:bg-gray-950`}>
                    <div className={WRAP}>
                        <div className="mb-10 md:mb-14 max-w-3xl">
                            <h2 className={H2}>Same resume. A very different path to the recruiter.</h2>
                            <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-slate-400">
                                Most applications are read by software first. A referral changes who looks at your profile, and why they trust it.
                            </p>
                        </div>
                        <CompareTable />
                    </div>
                </section>

                {/* ═════════ PRODUCT: WHAT YOU GET ═════════ */}
                <section className={`${SECTION} border-y border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-900`}>
                    <div className={WRAP}>
                        <h2 className={`${H2} max-w-3xl mb-10 md:mb-14`}>Everything between &ldquo;I want this job&rdquo; and the interview</h2>
                        <div className="grid gap-4 md:grid-cols-3 md:grid-rows-2">
                            {/* Referrals: the main thing */}
                            <div className="md:col-span-2 md:row-span-2 flex flex-col rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-6 sm:p-8">
                                <h3 className={`${display.className} text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white`}>Ask an employee, not a form</h3>
                                <p className="mt-3 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-400">
                                    Pick a role, send a short note, and a verified employee at that company decides whether to submit your resume. You see where your request stands.
                                </p>
                                <div className="mt-8 flex-1 rounded-xl bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-800 p-4 sm:p-5">
                                    <p className="text-xs text-slate-400">Your request</p>
                                    <p className="mt-2 text-base leading-relaxed text-slate-700 dark:text-slate-200">
                                        Hi, I&apos;m applying for the Senior Frontend Engineer role. I have four years of React experience and have attached my resume. Would you be open to referring me?
                                    </p>
                                    <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                                        <Icon name="check" className="w-3.5 h-3.5" strokeWidth={2.5} /> Sent to a verified employee
                                    </p>
                                </div>
                                <Link href="/browse-jobs" className={`mt-6 inline-flex min-h-[44px] items-center gap-1.5 self-start text-sm font-semibold text-teal-700 dark:text-customBlue ${FOCUS}`}>
                                    Browse referral jobs <Icon name="arrow" className="w-4 h-4" strokeWidth={2} />
                                </Link>
                            </div>

                            <div className="flex flex-col rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-6">
                                <h3 className={`${display.className} text-xl font-bold tracking-tight text-slate-900 dark:text-white`}>Search across India</h3>
                                <p className="mt-2 flex-1 text-base leading-relaxed text-slate-600 dark:text-slate-400">Filter by skills, experience and city.</p>
                                <div className="mt-4 flex flex-wrap gap-1.5">
                                    {cities.slice(0, 5).map((c) => (
                                        <span key={c} className="rounded-md bg-slate-100 dark:bg-gray-800 px-2 py-1 text-xs text-slate-600 dark:text-slate-300">{c}</span>
                                    ))}
                                </div>
                                <Link href="/job_search" className={`mt-4 inline-flex min-h-[44px] items-center gap-1.5 self-start text-sm font-semibold text-teal-700 dark:text-customBlue ${FOCUS}`}>
                                    Search jobs <Icon name="arrow" className="w-4 h-4" strokeWidth={2} />
                                </Link>
                            </div>

                            <div className="flex flex-col rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-6">
                                <h3 className={`${display.className} text-xl font-bold tracking-tight text-slate-900 dark:text-white`}>Practise out loud</h3>
                                <p className="mt-2 flex-1 text-base leading-relaxed text-slate-600 dark:text-slate-400">&ldquo;Tell me about a project you are proud of.&rdquo; Answer, get feedback, try again.</p>
                                <Link href="/AI_mock_interview" className={`mt-4 inline-flex min-h-[44px] items-center gap-1.5 self-start text-sm font-semibold text-teal-700 dark:text-customBlue ${FOCUS}`}>
                                    AI mock interview <Icon name="arrow" className="w-4 h-4" strokeWidth={2} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ═════════ NUMBERS ═════════ */}
                <section className={`py-16 md:py-20 bg-white dark:bg-gray-950`}>
                    <div className={WRAP}>
                        <dl className="grid grid-cols-2 gap-y-10 md:grid-cols-4 md:divide-x md:divide-slate-200 dark:md:divide-gray-800">
                            {[
                                { value: '5,000+', label: 'Active job listings' },
                                { value: '100+', label: 'Successful placements' },
                                { value: '95%', label: 'Success rate' },
                                { value: '100+', label: 'Partner companies' },
                            ].map((stat) => (
                                <div key={stat.label} className="flex flex-col-reverse px-4 text-center md:px-8">
                                    <dt className="mt-2 text-sm text-slate-500 dark:text-slate-400">{stat.label}</dt>
                                    <dd className={`${display.className} text-4xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white tabular-nums`}>{stat.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </section>

                {/* ═════════ AI MOCK INTERVIEW ═════════ */}
                <section className={`${SECTION} border-y border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-900`}>
                    <div className={WRAP}>
                        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
                            <div className="order-2 lg:order-1">
                                <div className="mx-auto max-w-md rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-6">
                                    <Image src="/refined.svg" alt="Preparing for an interview with AI practice" width={480} height={480} className="h-auto w-full" />
                                </div>
                            </div>
                            <div className="order-1 lg:order-2">
                                <h2 className={H2}>Walk into the interview already warmed up</h2>
                                <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-slate-400">
                                    The AI asks realistic questions for your target role. Answer them, read the feedback, and go again until the answers feel natural.
                                </p>
                                <ul className="mt-6 space-y-3">
                                    {['Industry-specific interview questions', 'Real-time performance analysis', 'Personalised improvement tips', 'Behavioural and technical practice'].map((item) => (
                                        <li key={item} className="flex items-start gap-3 text-base text-slate-700 dark:text-slate-300">
                                            <Icon name="check" className="mt-1 w-4 h-4 flex-shrink-0 text-teal-600 dark:text-customBlue" strokeWidth={2.5} />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                    <Link href="/AI_mock_interview" className={BTN_PRIMARY}>
                                        Start practising <Icon name="arrow" className="w-4 h-4" strokeWidth={2} />
                                    </Link>
                                    <Link href="/blog/ai-mock-interview-how-to-prepare-and-practice" className={BTN_SECONDARY}>
                                        Read the prep guide
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ═════════ TWO SIDES ═════════ */}
                <section className={`${SECTION} bg-white dark:bg-gray-950`}>
                    <div className={WRAP}>
                        <h2 className={`${H2} max-w-3xl mb-10 md:mb-14`}>One network, two ways to join</h2>
                        <div className="grid overflow-hidden rounded-3xl md:grid-cols-2">
                            <div className="bg-slate-100 dark:bg-gray-900 p-8 md:p-12">
                                <h3 className={`${display.className} text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white`}>Looking for a job</h3>
                                <ul className="mt-6 space-y-3.5">
                                    {['Access roles you will not find on public boards', 'Reach employees who can submit your resume', 'Learn what the team and culture are really like'].map((item) => (
                                        <li key={item} className="flex items-start gap-3 text-base text-slate-700 dark:text-slate-300">
                                            <Icon name="check" className="mt-1 w-4 h-4 flex-shrink-0 text-teal-700 dark:text-customBlue" strokeWidth={2.5} />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <Link href="/login?role=job-seeker" className={`mt-8 ${BTN_PRIMARY}`}>
                                    Join as Job Seeker <Icon name="arrow" className="w-4 h-4" strokeWidth={2} />
                                </Link>
                            </div>
                            <div className="bg-slate-900 dark:bg-gray-800 p-8 md:p-12 text-white">
                                <h3 className={`${display.className} text-2xl sm:text-3xl font-bold tracking-tight`}>Working at a company that is hiring</h3>
                                <ul className="mt-6 space-y-3.5">
                                    {['Help someone land a great opportunity', 'Bring strong people into your team', 'Earn your employer’s referral bonus, where offered'].map((item) => (
                                        <li key={item} className="flex items-start gap-3 text-base text-slate-300">
                                            <Icon name="check" className="mt-1 w-4 h-4 flex-shrink-0 text-customBlue" strokeWidth={2.5} />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <Link href="/login?role=referrer" className={`mt-8 inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 px-6 min-h-[48px] text-base font-semibold text-white transition hover:bg-white/10 ${FOCUS}`}>
                                    Join as Referrer <Icon name="arrow" className="w-4 h-4" strokeWidth={2} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ═════════ HOW IT WORKS ═════════ */}
                <section className={`${SECTION} border-y border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-900`}>
                    <div className={WRAP}>
                        <h2 className={`${H2} max-w-3xl mb-12 md:mb-16`}>From sign-up to interview</h2>
                        <ol className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
                            {[
                                { title: 'Create your profile', desc: 'Add your skills, experience and the roles you want.' },
                                { title: 'Find the right roles', desc: 'Search by skills, city and experience level.' },
                                { title: 'Request a referral', desc: 'Ask a verified employee to submit your resume.' },
                                { title: 'Prepare and interview', desc: 'Practise with the AI mock interview, then show up ready.' },
                            ].map((s, i) => (
                                <li key={s.title} className="border-t-2 border-slate-900 dark:border-white pt-5">
                                    <span className={`${display.className} text-sm font-bold text-slate-400`}>0{i + 1}</span>
                                    <h3 className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">{s.title}</h3>
                                    <p className="mt-2 text-base leading-relaxed text-slate-600 dark:text-slate-400">{s.desc}</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* ═════════ BLOG (server-rendered slot) ═════════ */}
                {blogSection}

                {/* ═════════ FAQ ═════════ */}
                <section className={`${SECTION} bg-white dark:bg-gray-950`}>
                    <div className={WRAP}>
                        <div className="grid gap-10 lg:grid-cols-[1fr_1.6fr] lg:gap-16">
                            <div>
                                <h2 className={H2}>Questions, answered</h2>
                                <p className="mt-5 text-base md:text-lg leading-relaxed text-slate-600 dark:text-slate-400">
                                    More in the <Link href="/faq" className="font-semibold text-teal-700 dark:text-customBlue hover:underline">full FAQ</Link>, or start with our <Link href="/blog/how-to-get-a-job-referral-in-india" className="font-semibold text-teal-700 dark:text-customBlue hover:underline">guide to getting a referral</Link>.
                                </p>
                            </div>
                            <div className="divide-y divide-slate-200 dark:divide-gray-800 border-y border-slate-200 dark:border-gray-800">
                                {faqData.map((faq, i) => (
                                    <div key={faq.q}>
                                        <button
                                            onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                            aria-expanded={openFaq === i}
                                            aria-controls={`home-faq-${i}`}
                                            className={`flex w-full min-h-[64px] items-center justify-between gap-4 py-4 text-left ${FOCUS}`}
                                        >
                                            <h3 className="text-base md:text-lg font-semibold text-slate-900 dark:text-white">{faq.q}</h3>
                                            <Icon name="chevron" className={`w-5 h-5 flex-shrink-0 text-slate-400 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} strokeWidth={2} />
                                        </button>
                                        <div id={`home-faq-${i}`} className={`grid transition-all duration-200 ${openFaq === i ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                                            <div className="overflow-hidden">
                                                <p className="pb-5 pr-8 text-base leading-relaxed text-slate-600 dark:text-slate-400">{faq.a}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ═════════ CTA ═════════ */}
                <section className="bg-white dark:bg-gray-950 pb-20 md:pb-28">
                    <div className={WRAP}>
                        <div className="rounded-3xl bg-slate-900 dark:bg-gray-900 dark:ring-1 dark:ring-gray-800 px-6 py-14 text-center md:px-12 md:py-20">
                            <h2 className={`${display.className} mx-auto max-w-2xl text-3xl sm:text-4xl lg:text-5xl font-bold tracking-[-0.025em] text-white text-balance`}>Ready to get referred?</h2>
                            <p className="mx-auto mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300">
                                Create a free profile, find roles that fit, and let a verified employee put your resume in front of the hiring team.
                            </p>
                            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
                                <Link href="/sign-up" className={BTN_PRIMARY}>Get started free</Link>
                                <Link href="/job_search" className={`inline-flex items-center justify-center rounded-xl border border-white/25 px-6 min-h-[48px] text-base font-semibold text-white transition hover:bg-white/10 ${FOCUS}`}>
                                    Browse jobs
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

            </main>
        </>
    );
}
